import { createFileRoute } from '@tanstack/react-router'
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { getPreviewImageForItemOptions, getRequestForCustomerByIdOptions } from "@/client/@tanstack/react-query.gen";
import { formatDate } from "@/lib/utils";
import { statusColors } from "@/lib/utils";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useParams } from "@tanstack/react-router";
import React from "react";
import { retryFunc } from '@/api/retry';
import { useCustomerId } from "@/components/customer-id-provider";
import { RequestItem } from '@/client/types.gen';
import { Loader2 } from 'lucide-react';
import { requireShopAuth } from '@/lib/shop-auth-guard';
import { localizedItemName } from '@/lib/app-language';
import { useAppLanguage } from '@/hooks/use-app-language';

type OrderStatusBadgeProps = {
  status: "request_created" | "waiting_for_information" | "in_processing" | "offer_in_progress" | "offer_available" | "offer_sent" | "offer_rejected" | "order_in_progress" | "in_delivery" | "delivered" | "closed";
};

const OrderStatusBadge = ({ status }: OrderStatusBadgeProps) => {
  const { t } = useTranslation();
  return (
    <Badge className={statusColors[status] || "bg-gray-400 text-white"}>
      {t(`orders.statuses.${status.toLowerCase()}`)}
    </Badge>
  );
};

function RouteComponent() {
  const { t } = useTranslation();
  const language = useAppLanguage();
  const params = useParams({ strict: false });
  const customerId = params.customerId || "";
  const requestId = params.requestId || "";
  const { setCustomerId, customerId: contextCustomerId } = useCustomerId();

  const { data: request } = useQuery({
    ...getRequestForCustomerByIdOptions({
      path: {
        customer_id: customerId,
        request_id: requestId,
      },
    }),
    retry: retryFunc,
  });

  React.useEffect(() => {
    if (customerId && customerId !== contextCustomerId) {
      setCustomerId(customerId);
    }
  }, [customerId, contextCustomerId, setCustomerId]);

  if (!request) {
    return null;
  }

  const itemsGroupedByMachine = request.items.reduce(
    (acc, item) => {
      acc[item.machineId] = acc[item.machineId] || {
        machineName: item.machineName,
        items: [],
      };
      acc[item.machineId].items.push(item);
      return acc;
    },
    {} as Record<
      string,
      {
        machineName: string;
        items: typeof request.items;
      }
    >
  );

  const hasItemComments = request.items.some(
    (item) => item.comment && item.comment.trim() !== ""
  );

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <h1 className="text-2xl font-bold mb-4">{t("orders.orderInfo")}</h1>
      <div className="grid grid-cols-4 gap-x-4 gap-y-1 mb-4">
        <span className="text-sm text-neutral-400">{t("orders.orderNo")}</span>
        <span className="text-sm">{request.orderKey || "MYXXXXXXXX"}</span>
        <span className="text-sm text-neutral-400">{t("orders.status")}</span>
        <OrderStatusBadge status={request.status ?? "request_created"} />

        <span className="text-sm text-neutral-400">{t("orders.orderDate")}</span>
        <span className="text-sm">{formatDate(request.createdAt ?? new Date(), language)}</span>
        <span className="text-sm text-neutral-400">{t("orders.lastStatusChange")}</span>
        <span className="text-sm">{formatDate(request.updatedAt ?? new Date(), language)}</span>

        {request.offerNumber ? (
          <>
            <span className="text-sm text-neutral-400">{t("orders.offerNo")}</span>
            <span className="text-sm">{request.offerNumber}</span>
          </>
        ) : (
          <span className="col-span-2" />
        )}
        <span className="text-sm text-neutral-400">{t("orders.createdBy")}</span>
        <span className="text-sm">{request.createdByName}</span>
      </div>
      
      {request.comment && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg border">
          <h3 className="font-semibold text-sm text-gray-700 mb-2">
            {t("orders.comment")}
          </h3>
          <p className="text-sm text-gray-600 whitespace-pre-wrap">
            {request.comment}
          </p>
        </div>
      )}
      
      {Object.entries(itemsGroupedByMachine).map(([machineId, data]) => (
        <div key={machineId} className="mb-6">
          <h2 className="font-semibold mb-2">{data.machineName}</h2>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("orders.itemPreview")}</TableHead>
                <TableHead>{t("orders.itemName")}</TableHead>
                <TableHead>{t("orders.itemCount")}</TableHead>
                {hasItemComments && (
                  <TableHead>{t("orders.itemComment")}</TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.map((item, idx) => (
                <TableRow key={item.asMiId + "-" + item.level1AncestorItemId || idx}>
                  <TableCell><RequestItemPreview item={item} /></TableCell>
                  <TableCell>
                    {localizedItemName(language, item.nameDe, item.nameEn)}
                  </TableCell>
                  <TableCell>{item.quantity}</TableCell>
                  {hasItemComments && (
                    <TableCell className="text-sm text-gray-600 whitespace-pre-wrap max-w-xs">
                      {item.comment || ""}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ))}
    </div>
  );
}

export const Route = createFileRoute(
  '/customers/$customerId/requests/$requestId/',
)({
  beforeLoad: requireShopAuth,
  component: RouteComponent,
})

function RequestItemPreview({ item }: { item: RequestItem }) {
  const { t } = useTranslation();
  const language = useAppLanguage();
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const { data, isLoading } = useQuery({
    ...getPreviewImageForItemOptions({
      path: {
        as_mi_id: item.asMiId,
      },
    }),
    retry: retryFunc,
  });

  if (isLoading) {
    return <div><Loader2 className="w-4 h-4 animate-spin" /></div>;
  }

  if (!data?.url) {
    return (
      <div className="w-10 h-10 bg-gray-200 rounded flex items-center justify-center text-xs text-gray-500">
        {t("orders.noImage")}
      </div>
    );
  }

  return (
    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
      <DialogTrigger asChild>
        <button className="focus:outline-none focus:ring-2 focus:ring-blue-500 rounded">
          <img 
            src={data.url} 
            alt={item.asMiId} 
            className="w-10 h-10 object-cover rounded cursor-pointer hover:opacity-80 transition-opacity" 
          />
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl">
        <div className="flex flex-col items-center">
          <img 
            src={data.url} 
            alt={item.asMiId} 
            className="max-w-full max-h-[70vh] object-contain mt-8" 
          />
          <p className="mt-4 text-sm text-gray-600">
            {localizedItemName(language, item.nameDe, item.nameEn) || item.asMiId}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}